import React from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { palette, FONT } from '../../styles/tourTheme';
import { pickTourDayCard, accentForDayKind } from '../../utils/calendarDayCard';
import {
  findTourDayByDate,
  getLodgingVenueForDay,
  getInterDayTravelSummary,
  formatLodgingDaySummary,
} from '../../utils/tourDayLodgingUtils';
import { formatContactsSummary } from '../../utils/calendarContacts';
import { hexWithAlpha } from '../../utils/calendarV2Prefs';
import { applyTimeFormatToText } from '../../utils/calendarHelpers';
import { formatScheduleAsPlainText } from '../ScheduleEditor';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function schedulePreview(raw) {
  const plain = formatScheduleAsPlainText(raw || '');
  if (!plain) return '—';
  const lines = plain.split('\n').map((l) => l.trim()).filter(Boolean).slice(0, 3);
  return lines.join('\n') || '—';
}

function lodgingLabel(tour, dateKey, card) {
  const lodging = getLodgingVenueForDay(findTourDayByDate(tour, dateKey).day);
  if (lodging) {
    return `${lodging.name || 'Hotel'}${lodging.city ? ` · ${lodging.city}` : ''}`;
  }
  return card?.hasHotel ? 'Rooms held' : '—';
}

/**
 * Week view as itinerary rows plus a right rail for lodging / travel / contact.
 */
export function CalendarWeekItinerary({
  days,
  eventsByDate,
  tourDayMetaByDate,
  selectedDateKey,
  selectedTourFilterId,
  theme,
  accent,
  onDayCellPress,
  tour = null,
  scheduleByDate = {},
  contactsByDate = {},
  showTravelDetail = true,
  timeFormat = '12-hour',
  onHotelPress,
}) {
  const isDarkMode = theme?.name === 'dark';
  const pal = palette(isDarkMode);
  const first = days[0]?.date;
  const last = days[days.length - 1]?.date;
  const rangeLabel = first && last
    ? `Week of ${first.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} — ${days.length} days`
    : '';

  const selectedMeta = tourDayMetaByDate?.[selectedDateKey];
  const selectedCard = pickTourDayCard(selectedMeta, selectedTourFilterId);
  const selectedTravel = showTravelDetail
    ? (selectedCard?.travelLine
      || (() => {
        const { dayIndex } = findTourDayByDate(tour, selectedDateKey);
        const hop = getInterDayTravelSummary(tour, dayIndex);
        return hop ? `${hop.fromLabel} → ${hop.toLabel}` : (selectedCard?.kind === 'travel' ? (selectedCard?.venueLine || 'Travel day') : '');
      })())
    : '';
  const selectedHotel = lodgingLabel(tour, selectedDateKey, selectedCard);
  const selectedLodgingDetail = formatLodgingDaySummary(tour, selectedDateKey);
  const selectedContacts = formatContactsSummary(contactsByDate?.[selectedDateKey] || '');

  return (
    <View style={styles.shell}>
      <ScrollView style={{ flex: 1, minWidth: 0 }} contentContainerStyle={styles.list}>
        {rangeLabel ? (
          <Text style={[styles.range, { color: pal.mute }]}>{rangeLabel}</Text>
        ) : null}
        {days.map(({ date, dateKey }) => {
          const meta = tourDayMetaByDate?.[dateKey];
          const card = pickTourDayCard(meta, selectedTourFilterId);
          const sel = dateKey === selectedDateKey;
          const hue = accent || pal.teal;
          const bar = accentForDayKind(card?.kind, pal.line);
          const extraEvents = (eventsByDate?.[dateKey] || []).filter((e) => e.eventType !== 'tour_day');
          const schedRaw = schedulePreview(scheduleByDate?.[dateKey]) !== '—'
            ? schedulePreview(scheduleByDate?.[dateKey])
            : extraEvents.slice(0, 3).map((ev) => ev.title).filter(Boolean).join('\n') || '—';
          const sched = applyTimeFormatToText(schedRaw, timeFormat);
          const travel = showTravelDetail
            ? (card?.travelLine || (card?.kind === 'travel' ? (card?.venueLine || 'Travel day') : '—'))
            : 'Hidden';
          const hotel = lodgingLabel(tour, dateKey, card);
          return (
            <TouchableOpacity
              key={dateKey}
              onPress={() => onDayCellPress?.(dateKey)}
              activeOpacity={0.85}
              accessibilityLabel={`${WEEKDAYS[date.getDay()]} ${date.getDate()}`}
              style={[
                styles.row,
                {
                  backgroundColor: sel ? hexWithAlpha(hue, isDarkMode ? 0.22 : 0.14) : pal.surf,
                  borderColor: sel ? hue : pal.line,
                  borderWidth: sel ? 2 : 1,
                  borderLeftWidth: card?.kind === 'show' || sel ? 3 : 1,
                  borderLeftColor: card?.kind === 'show' || sel ? (card?.kind === 'show' ? bar : hue) : pal.line,
                },
              ]}
            >
              <View style={styles.dateCol}>
                <Text style={[styles.dow, { color: pal.mute }]}>{WEEKDAYS[date.getDay()].slice(0, 3)}</Text>
                <Text style={[styles.dom, { color: pal.ink }]}>{date.getDate()}</Text>
              </View>
              <View style={[styles.placeCol, { borderLeftColor: pal.lineSoft }]}>
                <Text style={[styles.city, { color: pal.ink }]} numberOfLines={1}>
                  {card?.cityLine || (extraEvents[0]?.title) || 'Open day'}
                </Text>
                <Text style={[styles.venue, { color: pal.mute }]} numberOfLines={1}>
                  {card?.venueLine || ''}
                </Text>
              </View>
              <View style={styles.cols}>
                <View style={styles.col}>
                  <Text style={[styles.colLabel, { color: pal.faint }]}>Schedule</Text>
                  <Text style={[styles.colBody, { color: pal.inkSoft }]}>{sched}</Text>
                </View>
                <View style={styles.col}>
                  <Text style={[styles.colLabel, { color: pal.faint }]}>Travel</Text>
                  <Text style={[styles.colBody, { color: pal.inkSoft }]}>{travel}</Text>
                </View>
                <View style={styles.col}>
                  <Text style={[styles.colLabel, { color: pal.faint }]}>Hotel</Text>
                  {onHotelPress && hotel !== '—' ? (
                    <TouchableOpacity
                      onPress={(e) => {
                        e?.stopPropagation?.();
                        onHotelPress(dateKey);
                      }}
                      accessibilityRole="button"
                      accessibilityLabel={`Open lodging for ${dateKey}`}
                    >
                      <Text style={[styles.colBody, { color: pal.link || pal.inkSoft, textDecorationLine: 'underline' }]}>{hotel}</Text>
                    </TouchableOpacity>
                  ) : (
                    <Text style={[styles.colBody, { color: pal.inkSoft }]}>{hotel}</Text>
                  )}
                </View>
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
      {selectedDateKey ? (
        <View style={[styles.rail, { backgroundColor: pal.surf, borderLeftColor: pal.line }]}>
          <Text style={[styles.railEyebrow, { color: pal.faint }]}>Selected day</Text>
          <Text style={[styles.railTitle, { color: pal.ink }]} numberOfLines={2}>
            {selectedCard?.cityLine || selectedDateKey}
          </Text>
          <View style={styles.railBlock}>
            <Text style={[styles.colLabel, { color: pal.faint }]}>Lodging</Text>
            {onHotelPress && selectedHotel !== '—' ? (
              <TouchableOpacity
                onPress={() => onHotelPress(selectedDateKey)}
                accessibilityRole="button"
                accessibilityLabel="Open lodging"
              >
                <Text style={[styles.colBody, { color: pal.link || pal.ink, textDecorationLine: 'underline' }]}>
                  {selectedHotel}
                </Text>
              </TouchableOpacity>
            ) : (
              <Text style={[styles.colBody, { color: pal.ink }]}>{selectedHotel}</Text>
            )}
            {selectedLodgingDetail ? (
              <Text style={[styles.railDetail, { color: pal.mute }]}>{selectedLodgingDetail}</Text>
            ) : null}
          </View>
          <View style={styles.railBlock}>
            <Text style={[styles.colLabel, { color: pal.faint }]}>Travel</Text>
            <Text style={[styles.colBody, { color: pal.ink }]}>{selectedTravel || '—'}</Text>
          </View>
          <View style={styles.railBlock}>
            <Text style={[styles.colLabel, { color: pal.faint }]}>Contact</Text>
            <Text style={[styles.colBody, { color: pal.ink }]}>
              {selectedContacts || 'No contacts on file for this day.'}
            </Text>
          </View>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  shell: {
    flex: 1,
    flexDirection: 'row',
    minHeight: 0,
    alignItems: 'stretch',
  },
  list: {
    paddingHorizontal: 22,
    paddingTop: 18,
    paddingBottom: 24,
    gap: 6,
    flexGrow: 1,
  },
  range: {
    fontFamily: FONT.mono,
    fontSize: 11.5,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    paddingBottom: 6,
  },
  row: {
    flexDirection: 'row',
    gap: 16,
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'flex-start',
  },
  dateCol: { width: 84, flexShrink: 0 },
  dow: {
    fontFamily: FONT.mono,
    fontSize: 11,
    fontWeight: '500',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  dom: {
    fontFamily: FONT.sans,
    fontSize: 28,
    fontWeight: '600',
    letterSpacing: -0.5,
    lineHeight: 32,
  },
  placeCol: {
    width: 190,
    flexShrink: 0,
    borderLeftWidth: 1,
    paddingLeft: 16,
  },
  city: {
    fontFamily: FONT.sans,
    fontSize: 15.5,
    fontWeight: '600',
    letterSpacing: -0.2,
  },
  venue: {
    fontFamily: FONT.sans,
    fontSize: 13.5,
    marginTop: 2,
  },
  cols: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    gap: 18,
  },
  col: { flex: 1, minWidth: 0 },
  colLabel: {
    fontFamily: FONT.mono,
    fontSize: 10,
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    paddingBottom: 3,
  },
  colBody: {
    fontFamily: FONT.sans,
    fontSize: 13.5,
    lineHeight: 20,
  },
  rail: {
    width: 280,
    maxWidth: '34%',
    flexShrink: 0,
    borderLeftWidth: 1,
    paddingHorizontal: 16,
    paddingTop: 18,
    paddingBottom: 24,
    gap: 14,
  },
  railEyebrow: {
    fontFamily: FONT.mono,
    fontSize: 10,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  railTitle: {
    fontFamily: FONT.sans,
    fontSize: 16,
    fontWeight: '700',
    marginTop: -8,
  },
  railBlock: { gap: 4 },
  railDetail: {
    fontFamily: FONT.sans,
    fontSize: 12,
    lineHeight: 18,
    marginTop: 4,
  },
});

export default CalendarWeekItinerary;
